/**
 * Scopes widget CSS to one section (the "v2" scoper, used for code pasted or
 * saved with `scoper: 2`). Compared with the older scopers it:
 * - parses strings, comments and nested blocks properly;
 * - maps :root, html and body (also "html body", "body .x") onto the section;
 * - drops page-level layout from html/body (margins, 100vh, centering) and
 *   reports it, since a section can't be the page;
 * - renames @keyframes per section and updates animation references, so two
 *   widgets can't overwrite each other's animations;
 * - hoists @import (it only works at the top of a stylesheet) and keeps
 *   @font-face, @property and @page as they are;
 * - recurses into @media, @supports, @container and @layer;
 * - leaves CSS nesting inside a rule alone, since it is already scoped by its parent.
 */
import {
  CssNode, CssStyleRule, parseCss, serializeCss, splitDeclaration, splitTopLevel
} from './cssParser';

export const BASE_SCOPE_CLASSES = ['ai-widget-root', 'custom-widget-root'];

export type ScopeCssReport = {
  imports: string[];
  /** Declarations dropped from html/body/:root rules, e.g. "min-height: 100vh". */
  droppedPageDeclarations: string[];
  /** html/:root font-size, which would change rem units; not applied. */
  remBaseChanges: string[];
  renamedKeyframes: Record<string, string>;
  /** Rules that targeted html, body or :root and now style the section itself. */
  rootRules: number;
  fixedPositionSelectors: string[];
};

export type ScopeCssResult = { css: string; report: ScopeCssReport };

// Block at-rules whose rules style elements: their selectors are scoped too. (@scope's own
// prelude stays as written; the rules inside it are limited to the section like any other.)
const RECURSE_AT_RULES = new Set(['media', 'supports', 'container', 'layer', 'document', '-moz-document', 'scope', 'starting-style']);
const KEYFRAMES = new Set(['keyframes', '-webkit-keyframes', '-moz-keyframes', '-o-keyframes']);
const ANIMATION_PROPS = new Set(['animation', 'animation-name', '-webkit-animation', '-webkit-animation-name', '-moz-animation', '-moz-animation-name']);

// Properties that make sense on a page but not on a section. Dropped from html/body rules.
// overflow stays: authors set overflow-x: hidden on body to stop sideways scrolling, and
// keeping it on the section preserves that (the older scopers did the same).
const PAGE_ONLY_PROPS = new Set([
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'margin-block', 'margin-inline',
  'scroll-behavior', 'scroll-padding-top', 'overscroll-behavior',
  '-webkit-text-size-adjust', 'text-size-adjust', '-ms-text-size-adjust'
]);
// Centering a demo in the viewport ("min-height: 100vh; display: flex; align-items: center"):
// dropped together, and only when the rule really centers. A full-height body without
// centering is kept, since the section may be meant to fill the screen.
const CENTERING_PROPS = new Set(['display', 'place-items', 'place-content', 'align-items', 'justify-content', 'flex-direction']);
const VIEWPORT_SIZE_PROPS = new Set(['height', 'min-height', 'width', 'min-width']);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const unique = (list: string[]) => Array.from(new Set(list));

type RootMatch = { chain: string; qualified: boolean; rest: string } | null;

/**
 * If a selector starts with :root, html or body (optionally "html body",
 * "html > body", with qualifiers like ".dark" or "[lang=zh]"), returns that
 * leading chain as written, whether any part of it has qualifiers, and the rest:
 * "html[lang=zh] body .card" -> { chain: "html[lang=zh] body", qualified: true, rest: " .card" }.
 */
const matchRootPrefix = (selector: string): RootMatch => {
  const compound = /^(:root|html|body)((?:[.#][\w-]+|\[[^\]]*\]|:(?!:)[\w-]+(?:\([^)]*\))?)*)/i;
  let rest = selector;
  let chain = '';
  let qualified = false;
  let matched = false;
  for (;;) {
    const m = rest.match(compound);
    if (!m) break;
    matched = true;
    qualified = qualified || Boolean(m[2]);
    chain += m[0];
    rest = rest.slice(m[0].length);
    // "html body" or "html > body": take the next root-like compound too.
    const next = rest.match(/^\s*>?\s*(?=(?:html|body)\b)/i);
    if (!next) break;
    chain += next[0];
    rest = rest.slice(next[0].length);
  }
  if (!matched) return null;
  // "body-copy" or "htmlish" are element names that merely start with these words.
  if (/^[\w-]/.test(rest)) return null;
  return { chain, qualified, rest };
};

// isRoot: the rule styles the section's own element. fromPage: it was written for the page (html, body,
// :root) and now means the section, which the report mentions; rules already on the section class aren't.
const scopeSelector = (selector: string, scope: string): { selector: string; isRoot: boolean; fromPage?: boolean } => {
  const s = selector.trim();
  if (!s) return { selector: '', isRoot: false };
  const own = `.${scope}`;
  if (s === own || s.startsWith(`${own} `) || s.startsWith(`${own}.`) || s.startsWith(`${own}:`) || s.startsWith(`${own}[`) || s.startsWith(`${own}>`)) {
    return { selector: s, isRoot: s === own };
  }
  // The author anchored the selector on the base class: rename it instead of prefixing.
  for (const base of BASE_SCOPE_CLASSES) {
    const re = new RegExp(`\\.${escapeRegExp(base)}(?![\\w-])`, 'g');
    if (re.test(s)) {
      const renamed = s.replace(re, own);
      return { selector: renamed, isRoot: renamed === own };
    }
  }
  const root = matchRootPrefix(s);
  if (root) {
    const rest = root.rest.trim();
    // "body::before" belongs to the section itself; "body .x" and "body > .x" go inside it.
    const tail = !rest ? '' : root.rest.startsWith(':') ? root.rest : ` ${rest}`;
    // Qualifiers like html[lang=zh] or body.dark describe the page's state, which the
    // section can't have; keep them as a condition on the page around the section.
    if (root.qualified) return { selector: `${root.chain.trim()} ${own}${tail}`, isRoot: !rest, fromPage: !rest };
    return { selector: `${own}${tail}`, isRoot: !rest, fromPage: !rest };
  }
  return { selector: `${own} ${s}`, isRoot: false };
};

const renameAnimationValue = (value: string, names: Record<string, string>): string =>
  splitTopLevel(value, ',').map(part => part.replace(/(^|\s)(-?[_a-zA-Z][\w-]*)(?=\s|$)/g, (m, lead, ident) => (
    Object.prototype.hasOwnProperty.call(names, ident) ? `${lead}${names[ident]}` : m
  ))).join(',');

/** Filters declarations of a rule that targeted html/body/:root. */
const filterRootDeclarations = (rule: CssStyleRule, sources: string[], report: ScopeCssReport): void => {
  const isHtml = sources.some(src => /^(html|:root)\b/i.test(src));
  const decls = rule.children
    .map((n, index) => ({ n, index, parsed: n.type === 'declaration' ? splitDeclaration(n.text) : null }));
  const hasCentering = decls.some(d => d.parsed && ['place-items', 'place-content', 'align-items', 'justify-content'].includes(d.parsed.property.toLowerCase()))
    && decls.some(d => d.parsed && d.parsed.property.toLowerCase() === 'display' && /\b(flex|grid)\b/.test(d.parsed.value));
  const keep: CssNode[] = [];
  decls.forEach(({ n, parsed }) => {
    if (!parsed) { keep.push(n); return; }
    const prop = parsed.property.toLowerCase();
    if (prop === 'font-size' && isHtml) {
      report.remBaseChanges.push(`${prop}: ${parsed.value}`);
      return;
    }
    if (prop.startsWith('--')) { keep.push(n); return; }
    const viewportSized = VIEWPORT_SIZE_PROPS.has(prop) && /(\d(vh|vw|dvh|svh|lvh)\b|^100%$)/.test(parsed.value);
    if (PAGE_ONLY_PROPS.has(prop) || (hasCentering && (viewportSized || CENTERING_PROPS.has(prop)))) {
      report.droppedPageDeclarations.push(`${prop}: ${parsed.value}`);
      return;
    }
    keep.push(n);
  });
  rule.children = keep;
};

const collectFixed = (nodes: CssNode[], selector: string, report: ScopeCssReport): void => {
  nodes.forEach((n) => {
    if (n.type === 'declaration') {
      const d = splitDeclaration(n.text);
      if (d && d.property.toLowerCase() === 'position' && /^fixed\b/i.test(d.value) && !report.fixedPositionSelectors.includes(selector)) {
        report.fixedPositionSelectors.push(selector);
      }
    } else if (n.type === 'rule') {
      collectFixed(n.children, `${selector} ${n.selector}`, report);
    } else if (n.children) {
      collectFixed(n.children, selector, report);
    }
  });
};

const scopeNodes = (nodes: CssNode[], scope: string, report: ScopeCssReport, hoisted: CssNode[]): CssNode[] => {
  const out: CssNode[] = [];
  nodes.forEach((node) => {
    if (node.type === 'declaration') return; // a bare declaration at stylesheet level is invalid CSS
    if (node.type === 'atrule') {
      if (node.name === 'import') { report.imports.push(node.prelude); hoisted.push(node); return; }
      if (node.name === 'charset') return;
      if (node.name === 'namespace') { hoisted.push(node); return; }
      if (KEYFRAMES.has(node.name)) {
        const name = node.prelude.replace(/^["']|["']$/g, '');
        if (name.startsWith(`${scope}-`)) { out.push(node); return; } // already renamed
        const renamed = report.renamedKeyframes[name] || `${scope}-${name}`;
        report.renamedKeyframes[name] = renamed;
        out.push({ ...node, prelude: renamed });
        return;
      }
      if (RECURSE_AT_RULES.has(node.name) && node.children) {
        out.push({ ...node, children: scopeNodes(node.children, scope, report, hoisted) });
        return;
      }
      out.push(node); // @font-face, @page, @property, @counter-style, @layer statements, unknown
      return;
    }
    const scoped = splitTopLevel(node.selector, ',')
      .map(part => ({ part: part.trim(), ...scopeSelector(part, scope) }))
      .filter(p => p.selector);
    if (!scoped.length) return;
    const rootParts = scoped.filter(p => p.isRoot);
    const otherParts = scoped.filter(p => !p.isRoot);
    if (rootParts.length) {
      // html/body/:root now mean the section: drop what only makes sense for a page.
      // Reported only when the author wrote it for the page (the brief asks for variables on the section class).
      if (rootParts.some(p => p.fromPage)) report.rootRules++;
      const rootRule: CssStyleRule = { type: 'rule', selector: unique(rootParts.map(p => p.selector)).join(', '), children: node.children.slice() };
      filterRootDeclarations(rootRule, rootParts.map(p => p.part), report);
      collectFixed(rootRule.children, rootRule.selector, report);
      if (rootRule.children.length) out.push(rootRule);
    }
    if (otherParts.length) {
      const rule: CssStyleRule = { type: 'rule', selector: otherParts.map(p => p.selector).join(', '), children: node.children };
      collectFixed(rule.children, rule.selector, report);
      if (rule.children.length) out.push(rule);
    }
  });
  return out;
};

const renameAnimations = (nodes: CssNode[], names: Record<string, string>): CssNode[] => nodes.map((node) => {
  if (node.type === 'declaration') {
    const d = splitDeclaration(node.text);
    if (d && ANIMATION_PROPS.has(d.property.toLowerCase())) {
      return { type: 'declaration', text: `${d.property}: ${renameAnimationValue(d.value, names)}` };
    }
    return node;
  }
  if (node.type === 'rule') return { ...node, children: renameAnimations(node.children, names) };
  if (node.children && !KEYFRAMES.has(node.name)) return { ...node, children: renameAnimations(node.children, names) };
  return node;
});

export const scopeCss = (css: string, scopeClass: string): ScopeCssResult => {
  const report: ScopeCssReport = {
    imports: [], droppedPageDeclarations: [], remBaseChanges: [], renamedKeyframes: {}, rootRules: 0, fixedPositionSelectors: []
  };
  if (!css || !css.trim()) return { css: '', report };
  const hoisted: CssNode[] = [];
  let nodes = scopeNodes(parseCss(css), scopeClass, report, hoisted);
  if (Object.keys(report.renamedKeyframes).length) nodes = renameAnimations(nodes, report.renamedKeyframes);
  return { css: serializeCss([...hoisted, ...nodes]), report };
};
